import { queryClient } from '@/lib/query-client';
import { getRegisteredRouter } from '@/lib/router-registry';
import { useAuthStore } from '@/stores/auth-store';
import { ROUTES } from '@/constants/routes';

let teardownInFlight: Promise<void> | null = null;

/**
 * The single dead-session teardown: clear the auth store, land on /login,
 * and only THEN drop the query cache.
 *
 * Ordering is load-bearing: clearing the cache while the authenticated tree
 * is still mounted flashes the root ErrorFallback (readers see an emptied
 * cache). The cache clear itself is non-negotiable — without it, a re-login
 * within staleTime would let the route guards' ensureQueryData authorize the
 * new session with the PREVIOUS user's cached profile — so it runs even when
 * navigation rejects: cross-user leak protection outranks a one-frame flash.
 *
 * Single-flight: concurrent 401s (a dashboard's parallel queries all failing
 * at once) must not stack N navigations and N cache clears — a late clear()
 * could wipe data a fast re-login just fetched.
 */
export function teardownSessionToLogin(): Promise<void> {
  if (teardownInFlight) {
    return teardownInFlight;
  }

  const router = getRegisteredRouter();
  if (!router) {
    throw new Error('Session teardown needs the app router, and none is registered');
  }

  useAuthStore.getState().clearAuth();

  // ignoreBlocker: a dead session is not a navigation the user may veto —
  // an unsaved-changes blocker answering "Stay" would strand a logged-out
  // page and clear the cache under the still-mounted tree.
  teardownInFlight = router
    .navigate({ to: ROUTES.LOGIN, ignoreBlocker: true })
    .catch(() => {})
    .finally(() => {
      queryClient.clear();
      teardownInFlight = null;
    });

  return teardownInFlight;
}
